/**
 * Live status of a turn that is still running: what the coach is doing right now, in words, and for how long.
 *
 * The bubble used to show a fixed "Thinking…" until the first answer token (20-60 s when the coach asks an expert), with the steps,
 * the reasoning text and the tool list in a collapsed panel. `buildLiveStatus` turns that same stream state into one line:
 * the latest step as a sentence, a one-line preview of the coach's own latest reasoning (the full text stays in the panel),
 * and the elapsed time, which the widget ticks every second so the line moves even when the stream is silent.
 */

export const PREVIEW_CHARS = 120;
const DEFAULT_LABEL = 'Thinking…';
// Steps that describe the framework, not the work: never the headline.
const META_STEP = /^(Turn done|Complete —|Retrieving context|✅ Agent finished|💭 LLM turn)/;

const clean = (text) => String(text == null ? '' : text).replace(/\s+/g, ' ').trim();

/** The latest step as a sentence ("🔧 Using tool: web__SEARCH" keeps its tool name; a leading arrow or emoji is dropped). */
export function latestStepLabel(steps) {
  const list = Array.isArray(steps) ? steps : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const raw = typeof list[i] === 'string' ? list[i] : (list[i] && (list[i].text || list[i].step || list[i].description)) || '';
    const text = clean(raw);
    if (!text || META_STEP.test(text)) continue;
    return text.replace(/^[→🔧✓💭\s]+/u, '').replace(/^Using tool:\s*/, 'Using ') || DEFAULT_LABEL;
  }
  return DEFAULT_LABEL;
}

/** One line of what the coach is reasoning right now: the end of the latest text, cut to PREVIEW_CHARS with a leading ellipsis. */
export function reasoningPreview(thinkingText, liveText, max = PREVIEW_CHARS) {
  const source = clean(thinkingText) || clean(liveText);
  if (!source) return '';
  const lastLine = source.length <= max ? source : `…${source.slice(-(max - 1)).replace(/^\S*\s/, '')}`;
  return lastLine.length > max ? `…${lastLine.slice(-(max - 1))}` : lastLine;
}

export function formatElapsed(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s`;
}

/** {label, preview, elapsedText} for the running turn. `elapsedS` is the larger of the client timer and the server heartbeat. */
export function buildLiveStatus({ steps, thinkingText, liveText, elapsedS }) {
  return {
    label: latestStepLabel(steps),
    preview: reasoningPreview(thinkingText, liveText),
    elapsedText: formatElapsed(elapsedS),
  };
}
